import { usePracticeBannerData } from '../../../../PracticeBannerProvider.graphql';
import { TAB_BAR_HEIGHT } from '../../../../layouts/mobile/MobileTabs';
import { useIsChairside } from '../../../../utils/chairside.hooks';
import type { PatientListRawPatient } from '../../types/Patients.types';
import { patientSortTypeAtom } from '../../types/Patients.types';
import { PatientListItemUtils } from '../../utils/PatientListItem.utils';
import { useQuery } from '@apollo/client';
import { BrowserAnalyticsClientFactory } from '@orthly/analytics/dist/browser';
import { PracticeScreen } from '@orthly/dentin';
import { graphql } from '@orthly/graphql-inline-react';
import { TOOLBAR_TABLET_DOWN_HEIGHT, useScreenIsMobile, useToolbarContainerHeight } from '@orthly/ui';
import { FlossPalette, styled, useScreenIsMd, Text, Skeleton } from '@orthly/ui-primitives';
import type { VirtualListHookResult } from '@orthly/veneer';
import {
    OrderListNoResultsItem,
    PRACTICE_OUTAGE_BANNER_HEIGHT,
    VIRTUAL_LIST_LOADING,
    VirtualList,
} from '@orthly/veneer';
import { useAtomValue } from 'jotai';
import React from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

const BasicListItemWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignContent: 'center',
    height: '100%',
    padding: '0 40px',
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
        padding: '0 16px',
    },
}));

const StyledListItemWrapper = styled(BasicListItemWrapper)({
    '&:hover': {
        background: FlossPalette.PRIMARY_BACKGROUND,
    },
});

const StyledListItem = styled('div')({
    position: 'relative',
    padding: '16px 0',
    borderBottom: `1px solid ${FlossPalette.STROKE_LIGHT}`,
});

type ListItemProps = { listItem?: PatientListRawPatient };
type UnwrappedListItemProps = { listItem: PatientListRawPatient };

function patientItemIsEqual(prev: ListItemProps, next: ListItemProps): boolean {
    return prev?.listItem?.id === next?.listItem?.id;
}

function patientsListItemPropsAreEqual(prev: ListItemProps, next: ListItemProps): boolean {
    const prevItem = prev.listItem;
    const nextItem = next.listItem;

    if (prevItem?.id !== nextItem?.id) {
        return false;
    }

    return patientItemIsEqual({ listItem: prevItem }, { listItem: nextItem });
}

const PracticePatientsListPatients_Query = graphql(`
    query listPracticePatientsWithSortTools($args: PatientListInputArgs!) {
        patients: listPracticePatientsWithSortTools(args: $args) {
            id
            first_name
            last_name
            gender
            birthday
        }
    }
`);

function useListViewData(startIndex: number, stopIndex: number): VirtualListHookResult<PatientListRawPatient> {
    const sort = useAtomValue(patientSortTypeAtom);

    const { data, loading } = useQuery(PracticePatientsListPatients_Query, {
        variables: {
            args: { with_replaced: false, sort },
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    const items = data?.patients || [];

    return {
        listItems: items.slice(startIndex, stopIndex).map(item => ({ ...item, key: item.id })),
        itemsLoading: loading,
        listItemCount: loading ? VIRTUAL_LIST_LOADING : items.length,
    };
}

const PatientsListItemLoading: React.VFC = () => {
    return (
        <BasicListItemWrapper>
            <StyledListItem>
                <Skeleton animation={'wave'} variant={'text'} style={{ width: '150px' }} />
                <Skeleton animation={'wave'} variant={'text'} style={{ width: '200px' }} />
            </StyledListItem>
        </BasicListItemWrapper>
    );
};

const PatientsListItemRender = React.memo<UnwrappedListItemProps>(props => {
    const { listItem: patient } = props;
    const navigate = useNavigate();

    const { title, subtitle } = PatientListItemUtils.getPatientListItemConfig(patient);

    const handleClick = () => {
        BrowserAnalyticsClientFactory.Instance?.track('Practice - Patient Page - Patient Selected', {
            patientId: patient.id,
        });

        navigate(`/${PracticeScreen.patients}/${patient.id}`);
    };

    return (
        <StyledListItemWrapper onClick={handleClick}>
            <StyledListItem>
                <Text variant={'body2'} medium>
                    {title}
                </Text>
                <Text variant={'caption'} color={'GRAY'}>
                    {subtitle}
                </Text>
            </StyledListItem>
        </StyledListItemWrapper>
    );
}, patientItemIsEqual);

const PatientsVirtualizedListItem = React.memo<ListItemProps>(
    ({ listItem }) => (listItem ? <PatientsListItemRender listItem={listItem} /> : <PatientsListItemLoading />),
    patientsListItemPropsAreEqual,
);

export const PatientsList: React.VFC = () => {
    const { contentPaddingTop } = usePracticeBannerData();
    const isChairside = useIsChairside();
    const isTablet = useScreenIsMd();
    const isMobile = useScreenIsMobile();
    const topBarHeight =
        useToolbarContainerHeight() +
        (!isTablet ? PRACTICE_OUTAGE_BANNER_HEIGHT : 0) +
        (!isChairside ? contentPaddingTop : 0) +
        (isTablet ? TAB_BAR_HEIGHT : 0) +
        (isMobile ? TOOLBAR_TABLET_DOWN_HEIGHT : 0);

    return (
        <VirtualList
            listItemHeight={80}
            toolbarHeight={topBarHeight}
            useItemData={useListViewData}
            ListItem={PatientsVirtualizedListItem}
            noResultsMessage={<OrderListNoResultsItem message={`No patients yet`} rootStyle={{ position: 'unset' }} />}
            showLoadBlocker={true}
            showErrorSnackbar={true}
        />
    );
};
